import { opBounds, unionBounds, boundsIntersect } from '@shared/ops.js';
import { isOpenShape } from '@shared/shapes.js';
import type { Bounds, Op } from './types';

const CELL = 512;
/** Ops covering more cells than this go in a separate list instead of the grid. */
const MAX_CELLS_PER_OP = 256;

const cellKey = (cx: number, cy: number) => `${cx},${cy}`;

/**
 * The drawing, in world space.
 *
 * Ops are kept in sequence order (that is the paint order) and indexed into a
 * uniform grid so a redraw only touches what is actually on screen — which is
 * what lets the canvas stay responsive when a pad holds tens of thousands of
 * strokes spread over a huge area.
 */
export class Scene {
  private ops = new Map<string, Op>();
  private ordered: Op[] = [];
  private orderDirty = false;
  private grid = new Map<string, Set<string>>();
  private oversized = new Set<string>();
  private boundsCache: Bounds | null = null;
  private boundsDirty = false;
  private opBoundsCache = new Map<string, Bounds>();

  /** Bumped on every change so the renderer knows to repaint. */
  version = 0;

  get size() {
    return this.ops.size;
  }

  private touch() {
    this.version += 1;
  }

  boundsOf(op: Op): Bounds {
    let b = this.opBoundsCache.get(op.id);
    if (!b) {
      b = opBounds(op) as Bounds;
      this.opBoundsCache.set(op.id, b);
    }
    return b;
  }

  private index(op: Op) {
    if (op.deleted) return;
    const b = this.boundsOf(op);
    const x0 = Math.floor(b.minX / CELL);
    const y0 = Math.floor(b.minY / CELL);
    const x1 = Math.floor(b.maxX / CELL);
    const y1 = Math.floor(b.maxY / CELL);

    if ((x1 - x0 + 1) * (y1 - y0 + 1) > MAX_CELLS_PER_OP) {
      this.oversized.add(op.id);
      return;
    }
    for (let cx = x0; cx <= x1; cx += 1) {
      for (let cy = y0; cy <= y1; cy += 1) {
        const key = cellKey(cx, cy);
        let bucket = this.grid.get(key);
        if (!bucket) {
          bucket = new Set();
          this.grid.set(key, bucket);
        }
        bucket.add(op.id);
      }
    }
  }

  private unindex(op: Op) {
    this.oversized.delete(op.id);
    const b = this.opBoundsCache.get(op.id);
    if (!b) return;
    const x0 = Math.floor(b.minX / CELL);
    const y0 = Math.floor(b.minY / CELL);
    const x1 = Math.floor(b.maxX / CELL);
    const y1 = Math.floor(b.maxY / CELL);
    if ((x1 - x0 + 1) * (y1 - y0 + 1) > MAX_CELLS_PER_OP) return;
    for (let cx = x0; cx <= x1; cx += 1) {
      for (let cy = y0; cy <= y1; cy += 1) {
        const key = cellKey(cx, cy);
        const bucket = this.grid.get(key);
        if (!bucket) continue;
        bucket.delete(op.id);
        if (!bucket.size) this.grid.delete(key);
      }
    }
  }

  /** Insert or replace an op. Returns false if it was already known and unchanged. */
  put(op: Op): boolean {
    const existing = this.ops.get(op.id);
    if (existing) {
      if (existing.seq === op.seq && !!existing.deleted === !!op.deleted) return false;
      this.unindex(existing);
      this.opBoundsCache.delete(op.id);
      const idx = this.ordered.indexOf(existing);
      if (idx >= 0) this.ordered.splice(idx, 1);
    }
    this.ops.set(op.id, op);
    this.ordered.push(op);
    this.orderDirty = true;
    this.index(op);
    this.boundsDirty = true;
    this.touch();
    return true;
  }

  /** Bulk insert, sorting and reindexing once at the end. */
  putMany(ops: Op[]) {
    for (const op of ops) {
      const existing = this.ops.get(op.id);
      if (existing) {
        this.unindex(existing);
        this.opBoundsCache.delete(op.id);
        const idx = this.ordered.indexOf(existing);
        if (idx >= 0) this.ordered.splice(idx, 1);
      }
      this.ops.set(op.id, op);
      this.ordered.push(op);
      this.index(op);
    }
    this.orderDirty = true;
    this.boundsDirty = true;
    this.touch();
  }

  get(id: string) {
    return this.ops.get(id);
  }

  /**
   * Re-index an op whose geometry was mutated in place. Moving something
   * changes which grid cells it belongs to, and its cached bounds are stale.
   */
  refresh(id: string) {
    const op = this.ops.get(id);
    if (!op) return;
    this.unindex(op);
    this.opBoundsCache.delete(id);
    this.index(op);
    this.boundsDirty = true;
    this.touch();
  }

  setDeleted(ids: string[], deleted: boolean) {
    let changed = false;
    for (const id of ids) {
      const op = this.ops.get(id);
      if (!op || !!op.deleted === deleted) continue;
      this.unindex(op);
      op.deleted = deleted;
      this.index(op);
      changed = true;
    }
    if (changed) {
      this.boundsDirty = true;
      this.touch();
    }
    return changed;
  }

  remove(id: string) {
    const op = this.ops.get(id);
    if (!op) return;
    this.unindex(op);
    this.ops.delete(id);
    this.opBoundsCache.delete(id);
    const idx = this.ordered.indexOf(op);
    if (idx >= 0) this.ordered.splice(idx, 1);
    this.boundsDirty = true;
    this.touch();
  }

  clear() {
    this.ops.clear();
    this.ordered = [];
    this.grid.clear();
    this.oversized.clear();
    this.opBoundsCache.clear();
    this.boundsCache = null;
    this.boundsDirty = false;
    this.touch();
  }

  private sorted(): Op[] {
    if (this.orderDirty) {
      this.ordered.sort((a, b) => (a.seq ?? Infinity) - (b.seq ?? Infinity));
      this.orderDirty = false;
    }
    return this.ordered;
  }

  /** Every live op, in paint order. */
  all(): Op[] {
    return this.sorted().filter((op) => !op.deleted);
  }

  /** Live ops whose bounds intersect `rect`, in paint order. */
  query(rect: Bounds): Op[] {
    const hits = new Set<string>();
    const x0 = Math.floor(rect.minX / CELL);
    const y0 = Math.floor(rect.minY / CELL);
    const x1 = Math.floor(rect.maxX / CELL);
    const y1 = Math.floor(rect.maxY / CELL);

    // A wildly zoomed-out viewport can span more cells than we hold ops, so
    // fall back to a linear scan when that is the cheaper option.
    const cellCount = (x1 - x0 + 1) * (y1 - y0 + 1);
    if (cellCount > this.grid.size * 2) {
      return this.sorted().filter((op) => !op.deleted && boundsIntersect(this.boundsOf(op), rect));
    }

    for (let cx = x0; cx <= x1; cx += 1) {
      for (let cy = y0; cy <= y1; cy += 1) {
        const bucket = this.grid.get(cellKey(cx, cy));
        if (bucket) for (const id of bucket) hits.add(id);
      }
    }
    for (const id of this.oversized) hits.add(id);

    const out: Op[] = [];
    for (const id of hits) {
      const op = this.ops.get(id);
      if (op && !op.deleted && boundsIntersect(this.boundsOf(op), rect)) out.push(op);
    }
    out.sort((a, b) => (a.seq ?? Infinity) - (b.seq ?? Infinity));
    return out;
  }

  /** Bounding box of everything currently visible on the pad. */
  bounds(): Bounds | null {
    if (!this.boundsDirty && this.boundsCache !== null) return this.boundsCache;
    let box: Bounds | null = null;
    for (const op of this.ops.values()) {
      if (op.deleted) continue;
      box = unionBounds(box, this.boundsOf(op)) as Bounds;
    }
    this.boundsCache = box;
    this.boundsDirty = false;
    return box;
  }

  /** Bounding box of one person's work — powers "take me back to my drawing". */
  boundsForUser(userId: string): Bounds | null {
    let box: Bounds | null = null;
    for (const op of this.ops.values()) {
      if (op.deleted || op.userId !== userId) continue;
      box = unionBounds(box, this.boundsOf(op)) as Bounds;
    }
    return box;
  }

  /** The most recent thing a user drew, for "return to where I was". */
  lastOpBoundsForUser(userId: string): Bounds | null {
    let best: Op | null = null;
    for (const op of this.ops.values()) {
      if (op.deleted || op.userId !== userId) continue;
      if (!best || (op.seq ?? 0) > (best.seq ?? 0)) best = op;
    }
    return best ? this.boundsOf(best) : null;
  }

  /** Ops belonging to `userId`, newest first — used by undo. */
  opsByUser(userId: string): Op[] {
    return this.sorted().filter((op) => op.userId === userId);
  }

  /**
   * Topmost live op within `radius` world units of a point. Used by the object
   * eraser, which removes whole strokes rather than nibbling pixels.
   */
  hitTest(x: number, y: number, radius: number): Op | null {
    const rect = { minX: x - radius, minY: y - radius, maxX: x + radius, maxY: y + radius };
    const candidates = this.query(rect);
    for (let i = candidates.length - 1; i >= 0; i -= 1) {
      const op = candidates[i];
      if (hitOp(op, x, y, radius)) return op;
    }
    return null;
  }
}

function pointSegmentDistSq(px: number, py: number, ax: number, ay: number, bx: number, by: number) {
  const dx = bx - ax;
  const dy = by - ay;
  const lenSq = dx * dx + dy * dy;
  if (lenSq === 0) return (px - ax) ** 2 + (py - ay) ** 2;
  let t = ((px - ax) * dx + (py - ay) * dy) / lenSq;
  t = Math.min(1, Math.max(0, t));
  return (px - (ax + t * dx)) ** 2 + (py - (ay + t * dy)) ** 2;
}

function hitOp(op: Op, x: number, y: number, radius: number): boolean {
  switch (op.kind) {
    case 'stroke': {
      const r = radius + op.size * 0.6;
      const rSq = r * r;
      const pts = op.points;
      if (pts.length === 3) return (x - pts[0]) ** 2 + (y - pts[1]) ** 2 <= rSq;
      for (let i = 0; i + 5 < pts.length; i += 3) {
        if (pointSegmentDistSq(x, y, pts[i], pts[i + 1], pts[i + 3], pts[i + 4]) <= rSq) return true;
      }
      return false;
    }
    case 'shape': {
      const r = radius + op.size * 0.6;
      if (isOpenShape(op.shape)) {
        return pointSegmentDistSq(x, y, op.x0, op.y0, op.x1, op.y1) <= r * r;
      }
      const minX = Math.min(op.x0, op.x1) - r;
      const maxX = Math.max(op.x0, op.x1) + r;
      const minY = Math.min(op.y0, op.y1) - r;
      const maxY = Math.max(op.y0, op.y1) + r;
      if (x < minX || x > maxX || y < minY || y > maxY) return false;
      if (op.fill) return true;
      // Hollow shapes are only hit near their outline.
      const innerX = Math.min(op.x0, op.x1) + r;
      const innerMaxX = Math.max(op.x0, op.x1) - r;
      const innerY = Math.min(op.y0, op.y1) + r;
      const innerMaxY = Math.max(op.y0, op.y1) - r;
      return !(x > innerX && x < innerMaxX && y > innerY && y < innerMaxY);
    }
    case 'stamp': {
      const r = op.size * 0.55 + radius;
      return (x - op.x) ** 2 + (y - op.y) ** 2 <= r * r;
    }
    case 'text': {
      const width = Math.max(1, op.text.length) * op.size * 0.62;
      return x >= op.x - radius && x <= op.x + width + radius
        && y >= op.y - op.size - radius && y <= op.y + op.size * 0.35 + radius;
    }
    case 'image':
      return x >= op.x - radius && x <= op.x + op.w + radius
        && y >= op.y - radius && y <= op.y + op.h + radius;
    case 'fill': {
      // Even-odd crossing count, matching how the fill is painted: a point
      // inside a hole is outside the fill, so clicking through a hole picks up
      // whatever is underneath instead.
      let inside = false;
      for (const ring of op.rings) {
        for (let i = 0, j = ring.length - 2; i < ring.length; j = i, i += 2) {
          const xi = ring[i]; const yi = ring[i + 1];
          const xj = ring[j]; const yj = ring[j + 1];
          if ((yi > y) !== (yj > y) && x < ((xj - xi) * (y - yi)) / (yj - yi) + xi) inside = !inside;
        }
      }
      return inside;
    }
    default:
      return false;
  }
}
